import { useMutation } from '@apollo/client';
import { STOP_CHARGING_SESSION } from '@mutations/stopChargingSession';
import { useNavigation } from '@react-navigation/native';
import { showToast } from 'components/common/CustomToast';
import { useEffect, useRef, useState } from 'react';
import { useChargingStore } from 'stores/chargingStore';
import { useParkingStore } from 'stores/parkingStore';
import { useRetryPaymentStore } from 'stores/retryPaymentStore';
import {
  Mutation,
  StopChargingSessionResponse,
} from 'types/generatedSchemaTypes';
import { RootNavProps } from 'types/navigation';
import useOngoingChargingSession from './useOngoingChargingSession';
import useOngoingParkingSession from './useOngoingParkingSession';
import { useUiStore } from 'stores/uiStore';

const useStopChargingMutation = () => {
  const { navigate } = useNavigation<RootNavProps>();
  const { allParkingSessions } = useOngoingParkingSession();
  const { ongoingChargingSession, ongoingChargingSessionRefetch } =
    useOngoingChargingSession();
  const { startChargingPolling } = useRetryPaymentStore();
  const chargingZone = ongoingChargingSession?.parkingZoneUid;
  const hasParkingSession = allParkingSessions?.find(
    (parkingSession) => parkingSession?.parkingZoneUid === chargingZone
  );

  const [asyncSessionId, setAsyncSessionId] = useState<string | null>(null);
  const asyncReceiptDataRef = useRef<StopChargingSessionResponse | null>(null);

  const asyncStoreStatus = useRetryPaymentStore((s) =>
    asyncSessionId
      ? s.getChargingSessionStatus(asyncSessionId, undefined)
      : undefined
  );

  useEffect(() => {
    if (!asyncSessionId) return;

    if (asyncStoreStatus === 'Success') {
      useUiStore.getState().setSheetModal('');
      useParkingStore.getState().setSelectedOngoingSession(undefined);
      useChargingStore.getState().setExpectingChargingReceipt(true);
      navigate('chargingReport', {
        receiptData: asyncReceiptDataRef.current || {},
      });
      setAsyncSessionId(null);
    } else if (asyncStoreStatus === 'Failed') {
      showToast('parking.receipt.retryUnsuccessfulDescription', 'error');
      useUiStore.getState().setSheetModal('');
      useParkingStore.getState().setSelectedOngoingSession(undefined);
      setAsyncSessionId(null);
    }
  }, [asyncStoreStatus, asyncSessionId, navigate]);

  const [stopChargingSessionV2, { loading }] = useMutation<Mutation>(
    STOP_CHARGING_SESSION,
    {
      onCompleted: async (data) => {
        await ongoingChargingSessionRefetch();
        const result = data?.stopChargingSessionV2;
        const paymentState = result?.paymentState;
        const chargingSessionId = result?.chargingSessionId;

        const isAsyncPayment =
          paymentState === 'Pending' ||
          paymentState === 'Retrying' ||
          paymentState === 'NotInitiated';

        if (isAsyncPayment && chargingSessionId) {
          startChargingPolling(chargingSessionId);
          asyncReceiptDataRef.current = result || null;
          setAsyncSessionId(chargingSessionId);
        } else {
          showToast(
            hasParkingSession
              ? 'success.chargingStopContinueParking'
              : 'success.chargingStop',
            'success'
          );
          useUiStore.getState().setSheetModal('');
          if (!hasParkingSession) {
            useChargingStore.getState().setExpectingChargingReceipt(true);
            const receiptData = result || {};
            navigate('chargingReport', { receiptData });
          }
          useParkingStore.getState().setSelectedOngoingSession(undefined);
        }
      },
      onError: () => showToast('error.chargingStop', 'error'),
    }
  );
  return { stopChargingSessionV2, loading };
};
export default useStopChargingMutation;
